// @ts-nocheck
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import QRCode from "react-qr-code";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Wallet, ShoppingBag, CheckCircle2 } from "lucide-react";
import { displayHandle } from "@/lib/handles";

const money = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;

export default function MyDigitalWalletPage() {
  const { user } = useAuth();

  useEffect(() => {
    document.title = "My Digital Wallet | RecruitLook Events";
  }, []);

  const { data: items = [], isLoading, refetch } = useQuery({
    queryKey: ["my-wallet-extras", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("purchased_extras")
        .select(
          "id, extra_name, extra_price, player_name, player_jersey_number, player_instagram_handle, player_x_handle, team_name, division, qr_code, payment_status, is_redeemed, redeemed_at, fulfilment_status, created_at, event_id, events(name, slug, start_date)"
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  // Stripe returns here after checkout; the webhook flips payment_status.
  useEffect(() => {
    if (!new URLSearchParams(window.location.search).has("success")) return;
    const t = setTimeout(() => refetch(), 2500);
    return () => clearTimeout(t);
  }, [refetch]);

  if (!user) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center space-y-4">
        <h1 className="text-2xl font-bold">My Digital Wallet</h1>
        <p className="text-muted-foreground">Sign in to see your extras and QR codes.</p>
        <Button asChild><Link to={`/login?next=${encodeURIComponent("/my-wallet")}`}>Sign in</Link></Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-10">
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
            <Wallet className="h-4 w-4" /> My Digital Wallet
          </div>
          <Button asChild variant="outline" size="sm" className="ml-auto">
            <Link to="/event-extras?only=player">
              <ShoppingBag className="w-3.5 h-3.5 mr-1.5" /> Browse player media
            </Link>
          </Button>
        </div>

        <h1 className="text-3xl font-bold mb-2">Your extras</h1>
        <p className="text-muted-foreground mb-8">
          Show the QR code at the event to redeem. Media deliverables are sent to the email on your purchase.
        </p>

        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center space-y-3">
              <p className="text-muted-foreground">Nothing in your wallet yet.</p>
              <Button asChild><Link to="/event-extras?only=player">Browse player media</Link></Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {items.map((it: any) => {
              const paid = ["paid", "free", "comped"].includes(it.payment_status);
              return (
                <Card key={it.id} className={it.is_redeemed ? "opacity-70" : ""}>
                  <CardContent className="p-5 flex gap-4">
                    <div className="shrink-0 bg-white p-2 rounded">
                      {it.qr_code ? <QRCode value={it.qr_code} size={92} /> : <div className="w-[92px] h-[92px] bg-muted rounded" />}
                    </div>
                    <div className="min-w-0 flex-1 space-y-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-semibold truncate">{it.extra_name}</p>
                        <span className="text-sm font-semibold whitespace-nowrap">{money(it.extra_price)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{it.events?.name}</p>
                      {it.player_name && (
                        <p className="text-xs">
                          {it.player_name}
                          {it.player_jersey_number ? ` · #${it.player_jersey_number}` : ""}
                          {it.team_name ? ` · ${it.team_name}` : ""}
                        </p>
                      )}
                      {(it.player_instagram_handle || it.player_x_handle) && (
                        <p className="text-xs text-muted-foreground truncate">
                          {[it.player_instagram_handle, it.player_x_handle].filter(Boolean).map(displayHandle).join(" · ")}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-1.5 pt-1">
                        <Badge variant={paid ? "default" : "secondary"}>{paid ? "Paid" : it.payment_status || "pending"}</Badge>
                        {it.fulfilment_status && <Badge variant="outline">{it.fulfilment_status}</Badge>}
                        {it.is_redeemed && (
                          <Badge variant="outline" className="gap-1"><CheckCircle2 className="w-3 h-3" /> Redeemed</Badge>
                        )}
                        {it.qr_code && <span className="text-[10px] text-muted-foreground font-mono">{it.qr_code}</span>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
